/**
 * GoersContentBrowser — Browse & Import GOERS Content as TWC Products
 *
 * Called by: DestinationManagement.jsx (GOERS Content tab)
 * Uses: goersApi from config/api.js → GET /goers/contents, /contents/:id/ticket-dates,
 *       /contents/:id/tickets, POST /goers/import
 * Import writes to tx_bp_product with MEMBER_CODE='twc', CATEGORY='GOERS Event'
 */

import React, { useState, useCallback } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Card, CardBody,
  Button, Badge, useToast, Spinner, SimpleGrid, Image,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Alert, AlertIcon, AlertDescription,
  Tag, TagLabel, Divider, useDisclosure, Link, Select,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDownload, FiCalendar, FiMapPin, FiEye } from 'react-icons/fi';
import { goersApi } from '../config/api';

const formatIDR = (num) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const StatusBadge = ({ status }) => {
  const map = { Published: 'green', Selling: 'green', Soldout: 'red', Draft: 'gray', Past: 'gray', Hidden: 'orange' };
  return <Badge colorScheme={map[status] || 'gray'} fontSize="xs">{status || 'Unknown'}</Badge>;
};

// memberId given (ITDC page) → import is locked to that member; omitted (TWC page) → admin picks.
const GoersContentBrowser = ({ memberId }) => {
  const toast = useToast();
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [ticketDates, setTicketDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importMember, setImportMember] = useState(memberId || 'twc'); // per-ticket member choice, set on hover/select
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchContents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await goersApi.get('/contents');
      const items = res?.data?.data || [];
      setContents(items);
      toast({ title: 'GOERS content loaded', description: `${items.length} events found`, status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      setError(msg);
      toast({ title: 'Failed to load', description: msg, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const fetchTicketDates = useCallback(async (content) => {
    setSelectedContent(content);
    setSelectedDate(null);
    setTickets([]);
    setIsLoadingDates(true);
    try {
      const res = await goersApi.get(`/contents/${content.content_id}/ticket-dates`, {
        params: { content_type: content.content_type || 'e' },
      });
      setTicketDates(res?.data?.data?.dates || []);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsLoadingDates(false);
    }
  }, [toast]);

  const fetchTickets = useCallback(async (date) => {
    setSelectedDate(date);
    setIsLoadingTickets(true);
    try {
      const res = await goersApi.get(`/contents/${selectedContent.content_id}/tickets`, {
        params: { content_type: selectedContent.content_type || 'e', date },
      });
      setTickets(res?.data?.data || []);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsLoadingTickets(false);
    }
  }, [selectedContent, toast]);

  const handleImportTicket = useCallback(async (ticket) => {
    setIsImporting(true);
    try {
      const res = await goersApi.post('/import', {
        contentId: selectedContent.content_id,
        contentType: selectedContent.content_type || 'e',
        ticketId: ticket.ticket_id,
        ticketDate: selectedDate,
        ticketName: ticket.ticket_name,
        price: ticket.ticket_selling_price || 0,
        description: selectedContent.content_description || '',
        contentName: selectedContent.content_name,
        memberCode: importMember,
      });
      toast({ title: 'Imported', description: res?.data?.message || 'Success', status: 'success', duration: 5000, isClosable: true });
      setSelectedContent(null);
      setSelectedDate(null);
      setTickets([]);
      setTicketDates([]);
      onClose();
    } catch (err) {
      toast({ title: 'Import failed', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsImporting(false);
    }
  }, [selectedContent, selectedDate, importMember, toast, onClose]);

  return (
    <Box>
      <HStack mb={4} justify="space-between" wrap="wrap" gap={2}>
        <Heading size="md">GOERS Content Browser</Heading>
          <Button leftIcon={<FiRefreshCw />} colorScheme="blue" size="sm" onClick={fetchContents} isLoading={isLoading} loadingText="Loading...">
            Refresh Contents
          </Button>
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Browse available events from GOERS. Select an event, pick a date, choose a ticket, pick the owning member
        (TWC or ITDC), and import.
        {' '}The date here only selects the price variant — the buyer chooses their actual visit date at purchase.
      </Text>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <VStack py={10}>
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.500">Loading GOERS contents...</Text>
        </VStack>
      ) : contents.length === 0 ? (
        <Card bg="white" borderRadius="16px" boxShadow="sm">
          <CardBody>
            <VStack py={8} spacing={3}>
              <Text color="gray.400" fontSize="lg">No GOERS events loaded</Text>
              <Text color="gray.400" fontSize="sm">Click "Refresh Contents" to fetch from GOERS API.</Text>
              <Button size="sm" colorScheme="blue" onClick={fetchContents}>Load Contents</Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <Card bg="white" borderRadius="16px" boxShadow="sm">
          <CardBody p={0}>
            <TableContainer maxH="65vh" overflowY="auto">
              <Table size="sm" variant="striped">
                <Thead position="sticky" top={0} bg="gray.50">
                  <Tr>
                    <Th>Event Name</Th>
                    <Th>Type</Th>
                    <Th>Price Range</Th>
                    <Th>Status</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {contents.map((c) => (
                    <Tr key={c.content_id}>
                      <Td>
                        <HStack>
                          {c.content_img_url && <Image src={c.content_img_url} boxSize="40px" borderRadius="md" objectFit="cover" />}
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold" fontSize="sm">{c.content_name}</Text>
                            {c.content_location?.name && (
                              <Text fontSize="xs" color="gray.500"><FiMapPin style={{ display: 'inline', marginRight: 4 }} />{c.content_location.name}</Text>
                            )}
                          </VStack>
                        </HStack>
                      </Td>
                      <Td><Badge fontSize="xs">{c.content_type || 'event'}</Badge></Td>
                      <Td fontSize="sm">{formatIDR(c.content_min_price)} – {formatIDR(c.content_max_price)}</Td>
                      <Td><StatusBadge status={c.content_status} /></Td>
                      <Td>
                        <Button size="xs" colorScheme="blue" variant="outline" leftIcon={<FiEye />}
                          onClick={() => { fetchTicketDates(c); onOpen(); }}
                          isLoading={isLoadingDates && selectedContent?.content_id === c.content_id}>
                          Select
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      {/* Detail / Ticket Selection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedContent?.content_name || 'Event Detail'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedContent && (
              <VStack align="stretch" spacing={4}>
                <HStack spacing={4}>
                  {selectedContent.content_img_url && <Image src={selectedContent.content_img_url} boxSize="120px" borderRadius="lg" objectFit="cover" />}
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" fontSize="lg">{selectedContent.content_name}</Text>
                    <Text fontSize="sm" color="gray.500">
                      <FiMapPin style={{ display: 'inline', marginRight: 4 }} />{selectedContent.content_location?.name || 'N/A'}
                    </Text>
                    <Text fontSize="sm">Price: {formatIDR(selectedContent.content_min_price)} – {formatIDR(selectedContent.content_max_price)}</Text>
                    <Text fontSize="sm">Organizer: {selectedContent.content_organizer || 'N/A'}</Text>
                    {selectedContent.content_widget_href && (
                      <Link href={selectedContent.content_widget_href} isExternal color="blue.500" fontSize="sm">
                        View widget page ↗
                      </Link>
                    )}
                    <StatusBadge status={selectedContent.content_status} />
                  </VStack>
                </HStack>
                <Divider />
                {selectedContent.content_description && (
                  <Box fontSize="sm" color="gray.600" dangerouslySetInnerHTML={{ __html: selectedContent.content_description }}
                    maxH="120px" overflowY="auto" p={2} bg="gray.50" borderRadius="md" />
                )}
                <Divider />
                <Box>
                  <Text fontWeight="semibold" mb={2}><FiCalendar style={{ display: 'inline', marginRight: 4 }} />Available Dates</Text>
                  {isLoadingDates ? <Spinner size="sm" /> : ticketDates.length === 0 ? <Text fontSize="sm" color="gray.500">No dates.</Text> : (
                    <SimpleGrid columns={{ base: 3, md: 5 }} spacing={2}>
                      {ticketDates.map((d) => (
                        <Button key={d} size="xs" variant={selectedDate === d ? 'solid' : 'outline'}
                          colorScheme={selectedDate === d ? 'blue' : 'gray'}
                          onClick={() => fetchTickets(d)} isLoading={isLoadingTickets && selectedDate === d}>{d}</Button>
                      ))}
                    </SimpleGrid>
                  )}
                </Box>
                <Divider />
                {selectedDate && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>Tickets for {selectedDate}</Text>
                    {isLoadingTickets ? <Spinner size="sm" /> : tickets.length === 0 ? <Text fontSize="sm" color="gray.500">No tickets.</Text> : (
                      <VStack align="stretch" spacing={3}>
                        {tickets.map((t) => (
                          <Card key={t.ticket_id} variant="outline" size="sm">
                            <CardBody>
                              <HStack justify="space-between" wrap="wrap" gap={2}>
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="bold" fontSize="sm">{t.ticket_name}</Text>
                                  <Text fontSize="sm" color="gray.600">Price: {formatIDR(t.ticket_selling_price)}</Text>
                                  <HStack spacing={2} fontSize="xs" color="gray.500">
                                    <Text>Qty: {t.ticket_total === -1 ? 'Unlimited' : t.ticket_total}</Text>
                                    <Text>Avail: {t.ticket_available === -1 ? 'Unlimited' : t.ticket_available}</Text>
                                  </HStack>
                                </VStack>
                                <HStack>
                                  <StatusBadge status={t.ticket_status_name} />
                                  {!memberId && (
                                    <Select size="sm" width="80px" value={importMember}
                                      onChange={(e) => setImportMember(e.target.value)}
                                      aria-label="Import as member">
                                      <option value="twc">TWC</option>
                                      <option value="itdc">ITDC</option>
                                    </Select>
                                  )}
                                  <Button size="sm" colorScheme="green" leftIcon={<FiDownload />}
                                    isDisabled={t.ticket_status_name !== 'Selling'}
                                    onClick={() => handleImportTicket(t)} isLoading={isImporting} loadingText="Importing...">
                                    Import
                                  </Button>
                                </HStack>
                              </HStack>
                              {t.ticket_fields?.length > 0 && (
                                <HStack mt={2} spacing={1} wrap="wrap">
                                  <Text fontSize="xs" color="gray.500" fontWeight="semibold">Fields:</Text>
                                  {t.ticket_fields.map((f) => (
                                    <Tag key={f.key} size="sm" colorScheme="blue" variant="subtle">
                                      <TagLabel>{f.label}{f.type?.includes('required') ? ' *' : ''}</TagLabel>
                                    </Tag>
                                  ))}
                                </HStack>
                              )}
                            </CardBody>
                          </Card>
                        ))}
                      </VStack>
                    )}
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default GoersContentBrowser;
